import { datumKort } from "@/lib/datum";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { plural } from "@/components/afspraken/afsprakenUtils";
import { Sparkles, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { errMessage } from "@/components/LoadError";

const fmt = (iso) => datumKort(iso);
const TREND = { blijft: "text-rose-700", verbetert: "text-emerald-700", opgelost: "text-emerald-700", nieuw: "text-zinc-500" };

const Kop = ({ children }) => <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mt-4 mb-1">{children}</div>;
const Lijst = ({ items, testid }) => (
  <ul data-testid={testid} className="list-disc pl-5 space-y-0.5 text-sm text-zinc-800">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
);

function CoachInhoud({ a, locatie }) {
  const s = a.structured;
  return (
    <div data-testid="coach-inhoud">
      <div data-testid="coach-meta" className="text-xs text-zinc-500">
        Gemaakt <span className="font-mono">{fmt(a.generatedAt)}</span> door <span className="capitalize">{a.generatedBy}</span>, op basis van {plural(a.counts?.afspraken ?? 0, "afspraak", "afspraken")} en {plural(a.counts?.calls ?? 0, "call", "calls")}
      </div>
      <Kop>Waar staat {locatie}</Kop>
      <p data-testid="coach-waarstaat" className="text-sm text-zinc-800">{s.waarStaat}</p>
      <Kop>Afspraken nagekomen?</Kop>
      <Lijst items={s.nagekomen} testid="coach-nagekomen" />
      <Kop>Wat valt op</Kop>
      <Lijst items={s.watValtOp} testid="coach-watvaltop" />
      {s.erkenning && (<><Kop>Erkenning</Kop><p data-testid="coach-erkenning" className="text-sm text-zinc-800">{s.erkenning}</p></>)}
      {s.terugkerend?.length > 0 && (
        <>
          <Kop>Terugkerend</Kop>
          <div className="flex flex-wrap gap-1.5" data-testid="coach-terugkerend">
            {s.terugkerend.map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-900">
                {t.thema} {t.aantal}× in {t.periode}
                <span data-testid={`coach-trend-${i}`} className={`font-semibold ${TREND[t.trend] || TREND.nieuw}`}>{t.trend}</span>
              </span>
            ))}
          </div>
        </>
      )}
      <Kop>Vraag voor de volgende call</Kop>
      <p data-testid="coach-vraag" className="text-sm text-zinc-800">{s.vraag}</p>
      <Kop>Voorstel</Kop>
      <Lijst items={s.voorstel} testid="coach-voorstel" />
      <div data-testid="coach-voor-locatie" className="mt-4 rounded-md border-2 border-dashed border-zinc-300 p-3">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Voor de locatie — kan mee in de mail</div>
        <p className="text-sm text-zinc-800">{s.voorDeLocatie}</p>
      </div>
    </div>
  );
}

export function CoachCard({ locatie, onChanged }) {
  const [analyse, setAnalyse] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fout, setFout] = useState("");
  // R19: de kaart wordt per locatie opnieuw opgebouwd (key); een laat antwoord na een wissel wordt genegeerd.
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = useCallback(() => {
    if (!locatie) return;
    setLoaded(false); setAnalyse(null); setFout("");
    api.get(`/ai-analysis/latest?level=locatie&target=${encodeURIComponent(locatie)}`)
      .then((r) => { if (!alive.current) return; setAnalyse(r.data.analysis?.structured ? r.data.analysis : null); setLoaded(true); })
      .catch((e) => { if (!alive.current) return; setFout(errMessage(e)); setLoaded(true); });
  }, [locatie]);
  useEffect(load, [load]);

  const genereer = async () => {
    setBusy(true); setFout("");
    try {
      const r = await api.post("/ai-analysis", { level: "locatie", target: locatie });
      if (!alive.current) return;
      setAnalyse(r.data);
      onChanged?.(locatie);
    } catch (e) {
      if (!alive.current) return;
      setFout(e?.response?.data?.detail || errMessage(e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-[10px] border border-zinc-200 p-4" data-testid="coach-card">
      <div className="flex items-start justify-between gap-2">
        <h2 className="font-heading font-black text-base text-zinc-900">Coach-blik op {locatie}</h2>
        <span className="shrink-0 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-zinc-950 text-white">AI · TW1-stijl</span>
      </div>
      {fout && <div data-testid="coach-fout" className="mt-3 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-800">{fout}</div>}
      {loaded && !analyse && !busy && !fout && <p data-testid="coach-leeg" className="mt-3 text-sm text-zinc-500">Nog geen coach-blik voor {locatie}.</p>}
      {analyse && <div className="mt-2"><CoachInhoud a={analyse} locatie={locatie} /></div>}
      <div className="mt-4">
        {busy ? (
          <button disabled data-testid="coach-bezig" className="w-full px-3 py-2 rounded-md bg-zinc-200 text-zinc-500 text-sm font-semibold">De coach kijkt… Dit duurt meestal een halve tot een hele minuut.</button>
        ) : analyse ? (
          <button data-testid="coach-opnieuw" onClick={genereer} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-zinc-300 text-sm font-semibold text-zinc-800 hover:border-zinc-400">
            <RotateCcw className="h-4 w-4" /> Opnieuw laten kijken
          </button>
        ) : (
          <button data-testid="coach-start" onClick={genereer} disabled={!loaded} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-60">
            <Sparkles className="h-4 w-4" /> Laat de coach kijken
          </button>
        )}
      </div>
      <p className="mt-3 text-[11px] text-zinc-400">Oordeelt over afspraken en de locatie, nooit over personen van het OM-team.</p>
    </div>
  );
}
